import { useEffect, useState, useCallback } from "react";
import { Modal, Button, Tag, Tooltip, message } from "antd";
import { LinkOutlined, CopyOutlined, SendOutlined, ReloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { fetchInstallments, generateInstallmentLink } from "../services/proposalSendService";

/**
 * The installment schedule for one quotation — every planned payment, its due date, status and link.
 * The rep can generate/regenerate an unpaid installment's link and email it to the customer, copy it,
 * or open it. Paid installments are read-only. First payment activates the subscription; the rest just
 * clear the balance.
 */
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? String(d).slice(0, 10) : "On receipt");
const fmtExpiry = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "—");

const STATUS = {
  paid: { color: "green", label: "Paid" },
  unpaid: { color: "gold", label: "Unpaid" },
  failed: { color: "red", label: "Failed" },
  expired: { color: "default", label: "Expired" },
};

const InstallmentManager = ({ sendId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);       // installment id currently acting on
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!sendId) return;
    setLoading(true); setError("");
    try {
      setData(await fetchInstallments(sendId));
    } catch (e) {
      setError(e?.message || "Could not load installments");
    } finally {
      setLoading(false);
    }
  }, [sendId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const act = async (iid, opts, label) => {
    setBusyId(iid + (opts.regenerate ? ":re" : opts.email ? ":send" : ":gen"));
    try {
      const res = await generateInstallmentLink(sendId, iid, opts);
      message.success(res.message || label || "Done");
      await load();
    } catch (e) {
      message.error(e?.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const copy = async (link) => {
    try { await navigator.clipboard.writeText(link); message.success("Link copied"); }
    catch { message.error("Copy blocked by the browser"); }
  };

  const rows = data?.installments || [];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<span style={{ fontWeight: 700 }}>Installment schedule</span>}
      footer={<Button onClick={onClose}>Close</Button>}
      width={720}
      destroyOnHidden
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}><LoadingOutlined /> Loading…</div>
      ) : error ? (
        <div style={{ color: "#dc2626", padding: 12 }}>{error}</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", padding: "4px 2px 14px", fontSize: 13 }}>
            <span>Total: <b>{inr(data?.total)}</b></span>
            <span style={{ color: "#16a34a" }}>Received: <b>{inr(data?.received)}</b></span>
            <span style={{ color: "#b45309" }}>Remaining: <b>{inr(data?.remaining)}</b></span>
            <span style={{ color: "#6b7280" }}>{data?.paidCount || 0}/{data?.totalCount || rows.length} paid</span>
          </div>

          <div style={{ border: "1px solid #eef0f5", borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f7f8fc", textAlign: "left", color: "#6b7280" }}>
                  <th style={th}>#</th><th style={th}>Amount</th><th style={th}>Due</th>
                  <th style={th}>Remind</th><th style={th}>Status</th><th style={th}>Link</th><th style={{ ...th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const paid = r.payment_status === "paid";
                  const s = STATUS[r.expired && !paid ? "expired" : r.payment_status] || STATUS.unpaid;
                  const hasLink = Boolean(r.payment_link) && !r.expired;
                  return (
                    <tr key={r.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                      <td style={td}>{r.seq}</td>
                      <td style={{ ...td, fontVariantNumeric: "tabular-nums" }}>{inr(r.amount)}</td>
                      <td style={td}>{fmtDate(r.due_date)}</td>
                      <td style={td}>{r.reminder_before_days != null ? `${r.reminder_before_days}d before` : "—"}</td>
                      <td style={td}>
                        <Tag color={s.color} style={{ marginInlineEnd: 0 }}>{s.label}</Tag>
                        {!paid && r.payment_link_expires_at && (
                          <div style={{ fontSize: 10.5, color: r.expired ? "#9ca3af" : "#b45309", marginTop: 3 }}>
                            {r.expired ? "expired " : "till "}{fmtExpiry(r.payment_link_expires_at)}
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        {paid ? <span style={{ color: "#9ca3af" }}>—</span>
                          : hasLink ? (
                            <div style={{ display: "inline-flex", gap: 6 }}>
                              <Tooltip title="Open"><Button size="small" icon={<LinkOutlined />} onClick={() => window.open(r.payment_link, "_blank", "noopener")} /></Tooltip>
                              <Tooltip title="Copy"><Button size="small" icon={<CopyOutlined />} onClick={() => copy(r.payment_link)} /></Tooltip>
                            </div>
                          ) : <span style={{ color: "#b45309", fontSize: 12 }}>none / expired</span>}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {paid ? <span style={{ color: "#9ca3af" }}>—</span> : (
                          <div style={{ display: "inline-flex", gap: 6 }}>
                            <Tooltip title="Generate (if needed) & email the link to the customer">
                              <Button
                                size="small" type="primary" icon={busyId === r.id + ":send" ? <LoadingOutlined /> : <SendOutlined />}
                                loading={busyId === r.id + ":send"}
                                onClick={() => act(r.id, { email: true }, "Link emailed")}
                              >Send</Button>
                            </Tooltip>
                            <Tooltip title="Mint a fresh link (new expiry) without emailing">
                              <Button
                                size="small" icon={busyId === r.id + ":re" ? <LoadingOutlined /> : <ReloadOutlined />}
                                loading={busyId === r.id + ":re"}
                                onClick={() => act(r.id, { regenerate: true, email: false }, "New link ready")}
                              />
                            </Tooltip>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr><td style={{ ...td, color: "#9ca3af", textAlign: "center" }} colSpan={7}>No installment plan on this quotation.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 10, lineHeight: 1.6 }}>
            The first paid installment activates the subscription; the rest clear the balance. Expired links
            regenerate automatically when you Send. The customer also gets automatic reminders before each due date.
          </div>
        </>
      )}
    </Modal>
  );
};

const th = { padding: "8px 12px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" };
const td = { padding: "8px 12px", verticalAlign: "top" };

export default InstallmentManager;
